import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { AvatarVariant, ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { Pencil, Reset } from '@factorialco/factorial-one-react/icons/app'
import {
  TrainingClassFragment,
  TrainingDetailFragment,
  TrainingDetailParticipantsFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

type Props = {
  training: TrainingDetailFragment
  trainingClasses: TrainingClassFragment[]
  participants: TrainingDetailParticipantsFragment[] | []
  hasTrainingsSessionInstructorFF: boolean
  instructors: AvatarVariant[] | []
}

export const TrainingDetailHeader: FC<Props> = ({
  trainingClasses,
  training,
  participants,
  hasTrainingsSessionInstructorFF,
  instructors,
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const goToEditPage = () => {
    navigate(
      navigation.trainings.list.details_revamp.editTraining.view.fullPath({
        id: training.id,
      }),
      { state: { from: location.pathname } }
    )
  }

  const onPublish = () => {
    navigate(
      navigation.trainings.list.details_revamp.view.startTraining.fullPath({
        id: training.id,
      })
    )
  }

  const onRevert = () => {
    navigate(
      navigation.trainings.list.details_revamp.view.stopTraining.fullPath({
        id: training.id,
      })
    )
  }

  const avatarsList: AvatarVariant[] | [] = participants.length
    ? participants.map(({ access }) => ({
        firstName: access?.firstName ?? '',
        lastName: access?.lastName ?? '',
        type: 'person',
        src: buildUrl(access?.avatar ?? ''),
      }))
    : []

  const groupList = trainingClasses.map((group) => group.name)

  return (
    <ResourceHeader
      title={training.name}
      metadata={[
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.type'),
          value: {
            type: 'text',
            content: training.external
              ? i18n.t('trainings.trainings_revamp.training_detail.overview.external')
              : i18n.t('trainings.trainings_revamp.training_detail.overview.internal'),
          },
        },
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.groups'),
          value: {
            type: 'data-list',
            data: groupList,
          },
        },
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.participants'),
          value: {
            type: 'list',
            variant: 'person',
            avatars: avatarsList,
          },
        },
        ...(hasTrainingsSessionInstructorFF
          ? [
              {
                label: i18n.t(
                  'trainings.trainings_revamp.training_detail.header.metadata.instructors'
                ),
                value: {
                  type: 'list' as const,
                  variant: 'person' as const,
                  avatars: instructors,
                },
              },
            ]
          : []),
      ]}
      status={{
        label: '',
        text:
          training.status === 'active'
            ? i18n.t('trainings.trainings_revamp.training_detail.header.status.published')
            : i18n.t('trainings.trainings_revamp.training_detail.header.status.draft'),
        variant: training.status === 'active' ? 'positive' : 'neutral',
      }}
      primaryAction={
        training.status !== 'active'
          ? {
              label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.publish'),
              onClick: onPublish,
            }
          : undefined
      }
      secondaryActions={[
        training.status === 'active' && {
          label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.revert'),
          onClick: onRevert,
          variant: 'critical' as const,
          icon: Reset,
        },
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.edit'),
          icon: Pencil,
          onClick: goToEditPage,
        },
      ].filter(Boolean)}
    />
  )
}
